import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const FIELD_LABEL = { username: "Public name", name: "Name", email: "Email" };

const NameHistory = ({ rows }) => (
  <div data-testid="admin-user-name-history">
    <label className="overline">Name history</label>
    {rows.length === 0 ? (
      <p className="text-sm text-rf-ink-soft mt-2" data-testid="admin-user-name-history-empty">No changes recorded yet.</p>
    ) : (
      <ul className="mt-2 max-h-48 overflow-y-auto divide-y divide-rf-ink/10 text-sm">
        {rows.map((h, i) => (
          <li key={i} className="py-1.5" data-testid={`admin-user-name-history-row-${i}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-rf-orange mr-2">{FIELD_LABEL[h.field] || h.field}</span>
            <span className="line-through text-rf-ink-soft">{h.old || "—"}</span> → <b>{h.new || "—"}</b>
            <span className="block text-[11px] text-rf-ink-soft">
              {new Date(h.at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · {h.changed_by === "admin" ? `by admin${h.admin_email ? ` (${h.admin_email})` : ""}` : "by the user"}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function UserNamesDialog({ target, canEditUsername, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [username, setUsername] = useState("");
  const [picked, setPicked] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const { data: d } = await api.get(`/admin/users/${target.user_id}/byline-names`);
    setData(d);
    setPicked([]);
    setNewName(d.current || "");
    const { data: h } = await api.get(`/admin/users/${target.user_id}/name-history`);
    setHistory(h);
  };

  useEffect(() => {
    if (!target) return;
    setData(null);
    setUsername(target.username || "");
    load().catch(() => toast.error("Could not load names"));
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveUsername = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/users/${target.user_id}/username`, { username: username.trim() });
      toast.success(`Public cook name saved${r.recipes_updated ? ` — ${r.recipes_updated} recipe${r.recipes_updated === 1 ? "" : "s"} updated` : ""}`);
      await load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const replace = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/users/${target.user_id}/byline-names/replace`, { names: picked, new_name: newName });
      toast.success(`Updated ${r.updated} recipe${r.updated === 1 ? "" : "s"} to "by ${newName.trim()}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const toggle = (n) => setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-user-names-dialog">
        <DialogHeader>
          <DialogTitle>Names for {target?.email}</DialogTitle>
          <DialogDescription>Changes are made quietly — the user isn&apos;t emailed.</DialogDescription>
        </DialogHeader>
        {canEditUsername && (
          <div>
            <label className="overline">Public cook name (@username)</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="admin-user-names-username" value={username} onChange={(e) => setUsername(e.target.value)} maxLength={20} placeholder="blank = clear" className="input-rf font-mono" />
              <button type="button" data-testid="admin-user-names-username-save" onClick={saveUsername} disabled={busy} className="btn-amber !py-2 !px-4 text-sm whitespace-nowrap">Save</button>
            </div>
            <p className="text-[11px] text-rf-ink-soft mt-1">3–20 letters, numbers or underscore. Recipes showing the old public name update automatically.</p>
          </div>
        )}
        <div>
          <label className="overline">&ldquo;By …&rdquo; names on their recipes</label>
          {!data && <p className="text-sm text-rf-ink-soft mt-2">Loading…</p>}
          {data?.names.length === 0 && <p className="text-sm text-rf-ink-soft mt-2" data-testid="admin-user-names-empty">This user has no recipes yet.</p>}
          <div className="flex flex-wrap gap-2 mt-2">
            {data?.names.map((n) => (
              <label key={n.name || "_blank"} data-testid={`admin-user-names-option-${n.name || "blank"}`} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer text-sm font-semibold ${picked.includes(n.name) ? "bg-rf-amber/30 border-rf-amber" : "bg-white border-rf-ink/15"}`}>
                <input type="checkbox" checked={picked.includes(n.name)} onChange={() => toggle(n.name)} className="w-4 h-4 accent-rf-orange" />
                {n.name || <i>no name</i>} <span className="text-xs text-rf-ink-soft">({n.count})</span>
              </label>
            ))}
          </div>
          {data?.names.length > 0 && (
            <div className="flex gap-2 mt-3">
              <input data-testid="admin-user-names-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New name" className="input-rf" />
              <button type="button" data-testid="admin-user-names-apply" onClick={replace} disabled={busy || picked.length === 0 || !newName.trim()} className="btn-amber !py-2 !px-4 text-sm whitespace-nowrap disabled:opacity-50">Change</button>
            </div>
          )}
        </div>
        <NameHistory rows={history} />
      </DialogContent>
    </Dialog>
  );
}
